// Layer 4 (UI) — the Maintenance route (polish.P21, locked comp
// `.impeccable/mocks/decision/maintenance.html`, variant A): Downtime's twin.
// One header row carrying the plant's constraint, one status band, an 8/4
// panel row — the selected monitoring point's trend (BM01 trunnion bearing by
// default) beside what Advisory has handed to Maintenance — then the three
// pill tabs, each with its derived headline.
//
// The selected point lives here, not on a tab: the panel row draws it on every
// tab, the Condition monitoring table sets it, and the register's Condition
// cell sets it and opens that tab. Both ride the URL (`?tab=`, `?point=`).

import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  HelpAnchor,
  Pill,
  SurfacePageBody,
  SurfacePageHeader,
  SurfacePageLayout,
  SurfacePageTabs,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/design-system';
import { cn } from '@/shared/cn';
import { SURFACE_PAGE_TITLE_LARGE } from '@/shared/ui-class';
import { MeterBar } from '@/versions/v1/modules/_viz';
import { useDrawer } from '@/app/providers';
import { Commentable } from '@/shared/comments/Commentable';
import { ProvenanceChip } from '@/versions/v1/shared/ProvenanceChip';
import { projectTileBySlug } from '@/versions/v1/shared/project-seed';
import { projectSidebarHref } from '@/versions/v1/shared/project-sidebar-nav';
import { useRole } from '@/versions/v1/shared/RoleContext';
import { useScenario } from '@/versions/v1/shared/ScenarioProvider';
import { useAdvisory } from '@/versions/v1/shared/AdvisoryProvider';
import { fmeaActionApproval } from '@/versions/v1/shared/FmeaLearningEngine';
import { useAnalysisRun } from '@/versions/v1/shared/useAnalysisRun';
import { approvedPillText } from '../advisory/AgentInvestigation.transform';
import { DowntimeCard, SYN_CHIP } from '../downtime/DowntimeCard';
import { EDGE_TABLE, LEAD_ROW } from './MaintenanceTable';
import { CONDITION_LABEL } from './strategy.seed';
import { conditionTone } from './Strategy.transform';
import { ConditionDetail } from './ConditionDetail';
import { conditionDetail } from './ConditionDetail.transform';
import { MaintenanceBand } from './MaintenanceBand';
import { maintenanceBand } from './MaintenanceBand.transform';
import { PendingFromAdvisory } from './PendingFromAdvisory';
import { pendingFromAdvisory, pendingLeadCaseId, pendingLeadFixture } from './PendingFromAdvisory.transform';
import { PmScheduleTab } from './PmSchedule';
import { pmTasks } from './PmSchedule.transform';
import { StrategyTab } from './Strategy';
import {
  CM_SERIES_BY_POINT,
  MAINTENANCE_TABS,
  type ConditionMonitoringPoint,
  type MaintenanceTab,
  conditionMonitoringPoints,
  equipmentHealthMeters,
  lowestTwoOpenWork,
  maintenanceCopy,
  maintenanceTabHeadlines,
  isMaintenanceTab,
  pendingMechElecFmeaReviewCount,
  plantConstraint,
  tablesForMaintenanceTab,
} from './Scene.transform';

const HELP_DOC: Record<MaintenanceTab, string> = {
  assets: 'maintenance/strategy',
  cond: 'maintenance/equipment-health',
  pm: 'maintenance/pm-schedule',
};


export function MaintenanceScene() {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const project = projectTileBySlug(projectId);
  const plantName = project?.name ?? 'this plant';
  const copy = maintenanceCopy(plantName);
  const { open: openDrawer } = useDrawer();
  const { state } = useScenario();
  const advisory = useAdvisory();
  const [tab, setTab] = useState<MaintenanceTab>('assets');

  const cmPoints = conditionMonitoringPoints(state);
  // The default selection is the first point that already opened a case —
  // the point with something to read — and never a typed id.
  const [selectedId, setSelectedId] = useState<string>(
    cmPoints.find((point) => point.caseId)?.id ?? cmPoints[0]?.id ?? '',
  );
  const detail = conditionDetail(selectedId, cmPoints, state);
  const tasks = pmTasks();
  const band = maintenanceBand({ points: cmPoints, tasks, state });
  const constraint = plantConstraint(state);
  const headlines = maintenanceTabHeadlines(state);
  const tabItems = MAINTENANCE_TABS.map((item) => ({ ...item, count: headlines[item.id] }));

  // The lead action's TARP tier is its own analysis run's, as its case page reads it.
  const run = useAnalysisRun(pendingLeadFixture());
  const tarpTier = run.status === 'ready' ? run.result.diagnosis?.candidates[0]?.tarp?.tier ?? null : null;
  // Maintenance's approval on the case page meets the respond-by clock and
  // turns the lead's draft pill into the case gate's approved pill.
  const leadCaseId = pendingLeadCaseId();
  const leadApproval = leadCaseId ? fmeaActionApproval(advisory.fmeaLearning, leadCaseId) : undefined;
  const approvedCaseIds = useMemo(
    () => new Set(leadCaseId && leadApproval ? [leadCaseId] : []),
    [leadCaseId, leadApproval],
  );
  const pending = pendingFromAdvisory({ state, tarpTier, approvedCaseIds });
  const approvedPill = leadApproval ? approvedPillText(leadApproval.at, leadApproval.by) : null;

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (isMaintenanceTab(tabParam)) setTab(tabParam);
    const pointParam = searchParams.get('point');
    if (pointParam && pointParam in CM_SERIES_BY_POINT) setSelectedId(pointParam);
  }, [searchParams]);

  useEffect(() => {
    document.title = project ? `${copy.title} · ${project.name}` : `${copy.title} · OptiFlow`;
  }, [copy.title, project]);

  const selectTab = (value: string) => {
    if (!isMaintenanceTab(value)) return;
    setTab(value);
    const next = new URLSearchParams(searchParams);
    next.set('tab', value);
    setSearchParams(next, { replace: true });
  };

  const selectPoint = (pointId: string) => {
    setSelectedId(pointId);
    const next = new URLSearchParams(searchParams);
    next.set('point', pointId);
    setSearchParams(next, { replace: true });
  };

  /** The register's Condition cell: Condition monitoring with that point selected. */
  const openPoint = (pointId: string) => {
    setSelectedId(pointId);
    setTab('cond');
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'cond');
    next.set('point', pointId);
    setSearchParams(next, { replace: true });
  };

  const openCaseDrawer = (caseId: string, title: string) => {
    openDrawer('case-drawer', {
      kicker: 'Case',
      title,
      wide: true,
      data: { caseId },
    });
  };

  if (!project) {
    return (
      <SurfacePageLayout width="full">
        <SurfacePageBody>
          <SurfacePageHeader title="Plant not found" />
        </SurfacePageBody>
      </SurfacePageLayout>
    );
  }

  return (
    <SurfacePageLayout width="full">
      <Commentable name="ProjectMaintenanceScene">
        <SurfacePageBody className="space-y-3" width="full">
          <header className="flex flex-wrap items-center gap-x-6 gap-y-2" data-maintenance-header>
            <div className="mr-auto flex min-w-0 items-center gap-3">
              <h1 className={cn(SURFACE_PAGE_TITLE_LARGE, 'min-w-0')}>{copy.title}</h1>
              {SYN_CHIP}
            </div>
            <div className="flex items-center gap-3">
              <dl
                className="flex min-w-0 flex-col gap-px border-l border-[var(--hairline-strong)] pl-4 tabular-nums"
                data-header-fact="constraint"
              >
                <dt className="text-xs font-semibold uppercase tracking-[0.07em] text-muted">Constraint</dt>
                <dd className="text-[13.5px] font-semibold leading-tight text-ink-primary">{constraint.circuit}</dd>
                <dd className="whitespace-nowrap text-xs text-muted">{constraint.comparison}</dd>
              </dl>
              <HelpAnchor doc={HELP_DOC[tab]} label="Maintenance" />
            </div>
          </header>

          <Commentable name="MaintenanceBand">
            <MaintenanceBand cells={band} />
          </Commentable>

          <div className="grid grid-cols-1 items-stretch gap-3 xl:grid-cols-12">
            <div className="min-w-0 xl:col-span-8">
              <Commentable name="ConditionPanel">
                {detail ? <ConditionDetail read={detail} projectId={projectId ?? 'alpha'} /> : null}
              </Commentable>
            </div>
            <div className="min-w-0 xl:col-span-4">
              <Commentable name="PendingFromAdvisory">
                <PendingFromAdvisory
                  read={pending}
                  projectId={projectId ?? 'alpha'}
                  approvedPill={approvedPill}
                  onOpenCase={openCaseDrawer}
                />
              </Commentable>
            </div>
          </div>

          <SurfacePageTabs
            aria-label="Maintenance"
            items={tabItems}
            value={tab}
            onChange={selectTab}
            variant="ink"
            className="pt-1"
          />

          {tab === 'assets' ? <StrategyTab onSelectPoint={openPoint} /> : null}
          {tab === 'cond' ? (
            <EquipmentHealthTab points={cmPoints} selectedId={selectedId} onSelect={selectPoint} />
          ) : null}
          {tab === 'pm' ? <PmScheduleTab /> : null}
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}

function EquipmentHealthTab({
  points: cmPoints,
  selectedId,
  onSelect,
}: {
  points: readonly ConditionMonitoringPoint[];
  selectedId: string;
  onSelect: (pointId: string) => void;
}) {
  const { can } = useRole();
  const { projectId } = useParams<{ projectId: string }>();
  const tables = tablesForMaintenanceTab('cond');
  const healthTable = tables[0];
  const healthMeters = healthTable ? equipmentHealthMeters(healthTable) : [];
  const lowestWork = lowestTwoOpenWork();
  const pendingReviews = pendingMechElecFmeaReviewCount();
  const reviewSeat = can('fmea:review') && pendingReviews > 0;

  return (
    <Commentable name="EquipmentHealth">
      <div className="grid grid-cols-1 gap-3 min-[1400px]:grid-cols-12">
        <DowntimeCard
          title="Condition monitoring points"
          actions={
            <ProvenanceChip
              text="[syn]"
              title="The seven points transcribe the site's condition-monitoring inventory (A §M9 ¶4); each trend reads a synthetic historian series"
              compact
            />
          }
          className="px-0 pb-1 min-[1400px]:col-span-8 [&>header]:px-4"
        >
          <div className={EDGE_TABLE}>
            <Table wrap={false} className="tabular-nums">
              <THead>
                <TR>
                  <TH>Item</TH>
                  <TH>Monitoring type</TH>
                  <TH>Frequency</TH>
                  <TH>Alert threshold</TH>
                  <TH>Trend</TH>
                </TR>
              </THead>
              <TBody>
                {cmPoints.map((point) => (
                  <TR
                    key={point.id}
                    onClick={() => onSelect(point.id)}
                    aria-selected={point.id === selectedId}
                    data-condition-point={point.id}
                    className={point.id === selectedId ? cn('cursor-pointer', LEAD_ROW) : 'cursor-pointer'}
                  >
                    <TD className="min-w-[7.5rem] font-semibold !text-ink-primary">
                      {point.caseId ? (
                        <Link
                          to={`/projects/${projectId ?? 'alpha'}/advisory?case=${point.caseId}`}
                          className="!text-accent-700 hover:underline dark:!text-accent-400"
                        >
                          {point.item}
                        </Link>
                      ) : (
                        point.item
                      )}
                    </TD>
                    <TD className="min-w-[9rem]">{point.monitoringType}</TD>
                    <TD className="tabular">{point.frequency}</TD>
                    <TD className="min-w-[9rem]">{point.alertThreshold}</TD>
                    <TD className="w-[30%] min-w-[14rem]">
                      {point.statusChip ? (
                        <span className="inline-flex flex-col gap-1 text-[12.5px]">
                          <span>
                            <Pill tone={conditionTone(point.statusChip)}>
                              {CONDITION_LABEL[point.statusChip] ?? point.statusChip}
                            </Pill>
                            {point.trendReadout ? (
                              <span className={cn('ml-1.5', point.id === selectedId ? 'text-ink-primary' : 'text-ink-secondary')}>
                                {point.trendReadout}
                              </span>
                            ) : null}
                          </span>
                          {point.affectsLine ? (
                            <span className="text-xs font-medium text-[var(--state-warning)]">
                              {point.affectsLine}
                            </span>
                          ) : null}
                        </span>
                      ) : (
                        '—'
                      )}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </div>
        </DowntimeCard>

        {healthTable ? (
          <DowntimeCard
            title="Equipment health index"
            actions={
              <ProvenanceChip
                text="[syn]"
                title="Illustrative equipment-health index — seeded per asset with warn and bad bands; a plant instance derives it from downtime and condition-monitoring history"
                compact
              />
            }
            className="min-[1400px]:col-span-4"
          >
            <div className="grid grid-cols-1 gap-x-8 gap-y-2.5 md:grid-cols-2 min-[1400px]:grid-cols-1">
              {healthMeters.map((row) => (
                <MeterBar
                  key={row.id}
                  label={row.asset}
                  value={row.healthPct}
                  max={100}
                  tone={row.tone}
                  valueLabel={row.healthLabel}
                />
              ))}
            </div>
          </DowntimeCard>
        ) : null}

        {lowestWork.length > 0 ? (
          <DowntimeCard
            title="Lowest two open work"
            className={cn('px-0 pb-1 [&>header]:px-4', reviewSeat ? 'min-[1400px]:col-span-8' : 'min-[1400px]:col-span-12')}
          >
            <div className={EDGE_TABLE}>
              <Table wrap={false} className="tabular-nums">
                <THead>
                  <TR>
                    <TH>Asset</TH>
                    <TH className="text-right">Health</TH>
                    <TH>WO</TH>
                    <TH>Description</TH>
                    <TH>Priority</TH>
                    <TH>Status</TH>
                  </TR>
                </THead>
                <TBody>
                  {lowestWork.map((row) => (
                    <TR key={row.wo}>
                      <TD className="whitespace-nowrap font-semibold !text-ink-primary">{row.asset}</TD>
                      <TD className="tabular text-right">{row.healthPct}%</TD>
                      <TD className="whitespace-nowrap font-mono text-xs">{row.wo}</TD>
                      <TD>{row.description}</TD>
                      <TD>{priorityCell(row.priority)}</TD>
                      <TD>{row.status}</TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </div>
          </DowntimeCard>
        ) : null}

        {reviewSeat ? (
          <DowntimeCard title="Review seat" className="min-[1400px]:col-span-4">
            <p className="text-[13px] text-ink-primary">
              {pendingReviews} proposed change{pendingReviews === 1 ? '' : 's'} awaiting mech/elec review
            </p>
            <Link
              to={`${projectSidebarHref(projectId ?? 'alpha', 'advisory')}?tab=approvals`}
              className="mt-1.5 inline-block text-[13px] font-semibold text-accent-700 hover:underline dark:text-accent-400"
            >
              Advisory › Approvals
            </Link>
          </DowntimeCard>
        ) : null}
      </div>
    </Commentable>
  );
}


function priorityCell(value: string) {
  if (value === 'URGENT' || value === 'HIGH') {
    return <Pill tone="danger">{value}</Pill>;
  }
  if (value === 'MED') {
    return <Pill tone="warning">{value}</Pill>;
  }
  return value;
}
